import { useState, type FormEvent } from 'react'
import { Loader2Icon } from 'lucide-react'
import { useAuth } from '@/lib/firebase/auth'
import { authErrorMessage } from '@/lib/firebase/auth-errors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/**
 * Shown when the app is opened from a sign-in link on a browser that doesn't
 * remember the address it was sent to — Firebase needs it to redeem the link.
 */
export function EmailLinkConfirm() {
  const { emailLinkStatus, completeEmailLinkSignIn, cancelEmailLinkSignIn } = useAuth()

  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (emailLinkStatus === 'completing') {
    return (
      <div className="flex w-full items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" />
        Signing you in…
      </div>
    )
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    setError(null)
    setBusy(true)
    try {
      await completeEmailLinkSignIn(email)
      // On success the auth state flips and AppShell replaces this whole tree.
    } catch (err) {
      setError(authErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="w-full space-y-3 text-left">
      <p className="text-sm text-muted-foreground">
        Confirm the email address this sign-in link was sent to.
      </p>

      <div className="space-y-1.5">
        <Label htmlFor="auth-link-email">Email</Label>
        <Input
          id="auth-link-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          placeholder="you@example.com"
          required
          disabled={busy}
          autoFocus
        />
      </div>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <Button type="submit" className="w-full gap-2" disabled={busy}>
        {busy && <Loader2Icon className="size-4 animate-spin" />}
        Sign in
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-full"
        onClick={cancelEmailLinkSignIn}
        disabled={busy}
      >
        Use another sign-in method
      </Button>
    </form>
  )
}
